import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const VideoPopup = ({ isOpen, onClose }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isOpen) {
      // Play video when opening
      video.play().catch(() => {
        // Ignore play errors (user interaction required, etc.)
      });
    } else {
      // Pause and reset video when closing
      video.pause();
      video.currentTime = 0;
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="video-popup"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Video Container */}
      <div className="relative z-10 w-full max-w-4xl mx-4 sm:mx-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Close video"
          data-testid="close-video-btn"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Video */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
          <video
            ref={videoRef}
            className="w-full h-auto"
            controls
            playsInline
            preload="metadata"
          >
            <source src="/buildpilot-promo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        
        {/* Caption */}
        <p className="text-center text-white/60 text-sm mt-4">
          Press Esc or click outside to close
        </p>
      </div>
    </div>
  );
};

export default VideoPopup;
